import React, { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeCircles } from 'react-loader-spinner';

import Select from 'react-select';
import makeAnimated from 'react-select/animated';

import AppModal from './AppModal';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';

import { makeStyles } from '@mui/styles';
import { getUserDeviceReport } from '../../actions/MobileDevicesActions';
import { decToHex, formatMobileDeviceID } from '../../containers/UIhelper/UIhelper';

const animatedComponents = makeAnimated();

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

import ExcelJS from 'exceljs';

const xlsColumns = [
  { title: 'Device ID', key: 'deviceId', width: 20, sortable: true },
  { title: 'Month', key: 'monthStartDate', width: 15 },
  { title: 'First Use', key: 'monthFirstUse', width: 22, sortable: true },
  { title: 'First Use Latitude', key: 'firstUseLat', width: 16 },
  { title: 'First Use Longitude', key: 'firstUseLng', width: 16 },
  { title: 'Total Usage', key: 'totalUsage', width: 12, sortable: true },
];

// Render a single report cell consistently across the table, Excel and PDF
// exports. Device ids come back as decimals (same as the devices table) and the
// coordinates carry far more precision than is useful on screen.
const formatReportCell = (row, key) => {
  const value = row[key];
  if (key === 'deviceId') {
    return value != null ? formatMobileDeviceID(decToHex(value)) : '';
  }
  if (key === 'monthStartDate') {
    // Always show the month as "YYYY-MM" regardless of the date the value carries.
    return value != null ? String(value).substring(0, 7) : '';
  }
  if (key === 'firstUseLat' || key === 'firstUseLng') {
    return value != null ? Number(value).toFixed(6) : '';
  }
  return value;
};

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: '600px',
  },
  cell: {
    borderLeft: '1px solid #eee',
  },
});

// Device id and total usage are numeric; first use is a sortable datetime string.
const getSortValue = (row, key) => {
  const value = row[key];
  if (key === 'deviceId' || key === 'totalUsage') {
    return Number(value);
  }
  return value;
};

// Shared by the on-screen table and the Excel/PDF exports so they stay in sync.
const sortRows = (data, sortConfig) => {
  if (!sortConfig.key) {
    return data;
  }
  const factor = sortConfig.direction === 'asc' ? 1 : -1;
  return [...data].sort((a, b) => {
    const av = getSortValue(a, sortConfig.key);
    const bv = getSortValue(b, sortConfig.key);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
};

function UserDeviceReportTable({ data, sortConfig, onSort }) {
  const classes = useStyles();

  return (
    <TableContainer component={Paper} className={classes.container}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {xlsColumns.map((col, index) => (
              <TableCell
                key={`header_${index}`}
                style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                className={classes.cell}
                sortDirection={sortConfig.key === col.key ? sortConfig.direction : false}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sortConfig.key === col.key}
                    direction={sortConfig.key === col.key ? sortConfig.direction : 'asc'}
                    onClick={() => onSort(col.key)}
                  >
                    {col.title}
                  </TableSortLabel>
                ) : col.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow hover key={`${row.deviceId}_${index}`}>
              {xlsColumns.map((col) => (
                <TableCell key={col.key} className={classes.cell}>
                  {formatReportCell(row, col.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

UserDeviceReportTable.propTypes = {
  data: PropTypes.array,
  sortConfig: PropTypes.object,
  onSort: PropTypes.func,
};

function UserDeviceReportModal(props, context) {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const currentMonth = new Date().toISOString().substring(0, 7);
  const [fromMonth, setFromMonth] = useState(currentMonth);
  const [toMonth, setToMonth] = useState(currentMonth);
  const [selectAllDevices, setSelectAllDevices] = useState(true);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const dispatch = useDispatch();

  const onSort = useCallback((key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  }, []);

  // The active sort drives the table and both exports (Excel/PDF).
  const sortedData = useMemo(() => sortRows(data, sortConfig), [data, sortConfig]);

  const devices = useSelector((state) => state.mobile.devices.items);
  const deviceOptions = useMemo(
    () =>
      (devices || []).map((device) => ({
        value: device.device_id,
        label: formatMobileDeviceID(decToHex(device.device_id)),
      })),
    [devices]
  );

  const onFromMonthChange = useCallback((e) => {
    setFromMonth(e.target.value);
  }, []);

  const onToMonthChange = useCallback((e) => {
    setToMonth(e.target.value);
  }, []);

  const onToggleAllDevices = useCallback(() => {
    setSelectAllDevices((prev) => {
      if (!prev) {
        // Switching to "all" clears any specific selection.
        setSelectedDevices([]);
      }
      return !prev;
    });
  }, []);

  const onGenerateReport = useCallback(async () => {
    if (!props.selectedProject) return;
    if (fromMonth > toMonth) {
      alert('Start month must be before end month!');
      return;
    }
    // "Select all" sends the current customer's full (already-scoped) device
    // list so the report stays bound to this customer's devices.
    const deviceIds = selectAllDevices
      ? (devices || []).map((d) => d.device_id)
      : selectedDevices.map((d) => d.value);
    if (!selectAllDevices && !deviceIds.length) {
      alert('Please choose at least one device, or select all devices!');
      return;
    }
    setData([]);
    setIsLoading(true);
    try {
      const response = await dispatch(getUserDeviceReport({
        from: fromMonth,
        to: toMonth,
        deviceIds,
        customerId: props.selectedProject.CustomerID,
      }));
      if (response.status && response.data && response.data.length) {
        setData(response.data);
      } else {
        alert('This Report Is Empty!');
        setData([]);
      }
    } catch (error) {
      console.error('Error generating user device report: ', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, fromMonth, toMonth, selectAllDevices, selectedDevices, devices, props.selectedProject]);

  const reportPeriod = fromMonth === toMonth ? fromMonth : `${fromMonth} to ${toMonth}`;
  const exportFileName = `User Device Report - ${reportPeriod}`;

  const handleExportExcel = async () => {
    if (!data.length) {
      alert('No data to export!');
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('User Device Report');

    worksheet.columns = xlsColumns.map((col) => ({
      header: col.title,
      key: col.key,
      width: col.width,
    }));

    sortedData.forEach((item) => {
      const rowValues = {};
      xlsColumns.forEach((col) => {
        rowValues[col.key] = formatReportCell(item, col.key);
      });
      worksheet.addRow(rowValues);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${exportFileName}.xlsx`;
    link.click();
  };

  const exportPdf = () => {
    if (!data.length) {
      alert('No data to export!');
      return;
    }
    const tableBody = sortedData.map((row) =>
      xlsColumns.map((col) => formatReportCell(row, col.key))
    );
    const tableHeader = xlsColumns.map((col) => col.title);

    const doc = new jsPDF({ orientation: 'landscape' });
    const horizontalPadding = 10;
    doc.setFontSize(16);
    doc.text('User Device Report', horizontalPadding, 10);
    doc.setFontSize(12);
    doc.text(`Period: ${reportPeriod}`, horizontalPadding, 25);

    doc.autoTable({
      styles: { valign: 'center', fontSize: 8 },
      startX: 0,
      startY: 35,
      columns: tableHeader,
      body: tableBody,
      minCellWidth: 5,
      cellPadding: 2,
    });

    doc.save(`${exportFileName}.pdf`);
  };

  return (
    <AppModal
      closeIcon
      fullWidth
      maxWidth="xl"
      open={props.isVisible}
      title={context.t('user_device_report')}
      onClose={props.close}
      content={(
        <div style={{ height: '75vh', overflow: 'hidden' }}>
          {props.selectedProject && (
            <div style={{ display: 'flex' }}>
              <div>
                <Typography component="p" variant="body1">{context.t('create_report')}</Typography>
                <div style={{ margin: '10px 0' }}>
                  <label>{context.t('start_month')}:&nbsp;</label>
                  <input
                    type="month"
                    min="2016-01"
                    value={fromMonth}
                    onChange={onFromMonthChange}
                  />
                </div>
                <div style={{ margin: '10px 0' }}>
                  <label>{context.t('end_month')}:&nbsp;</label>
                  <input
                    type="month"
                    min="2016-01"
                    value={toMonth}
                    onChange={onToMonthChange}
                  />
                </div>
                <div className="checkbox-iq100b-mobile-user-list" style={{ margin: '10px 0' }}>
                  <label>{context.t('devices_list')}</label>
                  <div className="checkbox-iq100b-mobile-selected-row-type">
                    <input
                      type="checkbox"
                      onChange={onToggleAllDevices}
                      checked={selectAllDevices}
                    />
                    <label onClick={onToggleAllDevices}>&nbsp;{context.t('select_all_devices')}</label>
                  </div>
                  <Select
                    isDisabled={selectAllDevices}
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    isMulti
                    placeholder={selectAllDevices ? context.t('select_all_devices') : context.t('select')}
                    onChange={setSelectedDevices}
                    value={selectedDevices}
                    options={deviceOptions}
                  />
                </div>
                <div className="checkbox-iq100b-mobile-button-view">
                  <Button
                    disabled={isLoading}
                    variant="contained"
                    color="primary"
                    onClick={onGenerateReport}
                  >
                    {context.t('generate_report')}
                  </Button>
                </div>
                <div className="iq100b-export-buttons">
                  <Button
                    disabled={isLoading || !data.length}
                    style={{ width: 'max-content' }}
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={exportPdf}
                  >
                    {context.t('save_as_pdf')}
                  </Button>
                  <Button
                    disabled={isLoading || !data.length}
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleExportExcel}
                  >
                    {context.t('save_as_excel')}
                  </Button>
                </div>
              </div>
              <div style={{
                display: 'inline-flex',
                flex: 1,
                margin: 10,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {!isLoading && data.length ? (
                  <UserDeviceReportTable data={sortedData} sortConfig={sortConfig} onSort={onSort} />
                ) : isLoading && !data.length ? (
                  <ThreeCircles height="30" width="30" color={'#6090B1'} />
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}
    />
  );
}

UserDeviceReportModal.contextTypes = {
  t: PropTypes.func.isRequired,
};

UserDeviceReportModal.propTypes = {
  close: PropTypes.func,
  isVisible: PropTypes.bool,
  selectedProject: PropTypes.object,
};

export default React.memo(UserDeviceReportModal);
